import ApiEndPoints from "@/constants/apiEndpoints";
import api from "@/lib/api";
import { clearAuthCookies } from "@/lib/auth/cookies";
import { clearFlow } from "@/lib/auth/store";
import { csrfHeaders, handledErrorConfig, idempotencyHeaders } from "@/lib/api/headers";
import type {
  IAuthTokenData,
  IAuthSessionResult,
  ICompleteRegistrationRequest,
  IOtpRequestResult,
  IOtpVerifyResult,
  IPasswordLoginRequest,
  IResetTokenResult,
} from "@/types/auth";
import type { IApiEnvelope } from "@/types/common";

export async function requestOtp(
  mobile: string,
  idempotencyKey?: string,
): Promise<IOtpRequestResult> {
  const payload = await api.post<IApiEnvelope<IOtpRequestResult>>(
    ApiEndPoints.AUTH.OTP_REQUEST,
    { mobile },
    { headers: idempotencyHeaders(idempotencyKey), ...handledErrorConfig },
  );
  return payload.data!;
}

export async function resendOtp(
  mobile: string,
  channel: "sms" | "voice",
  idempotencyKey?: string,
): Promise<IOtpRequestResult> {
  const payload = await api.post<IApiEnvelope<IOtpRequestResult>>(
    ApiEndPoints.AUTH.OTP_RESEND,
    { mobile, channel },
    { headers: idempotencyHeaders(idempotencyKey), ...handledErrorConfig },
  );
  return payload.data!;
}

export async function verifyOtp(mobile: string, code: string): Promise<IOtpVerifyResult> {
  const payload = await api.post<IApiEnvelope<IOtpVerifyResult>>(
    ApiEndPoints.AUTH.OTP_VERIFY,
    { mobile, code },
    handledErrorConfig,
  );
  return payload.data!;
}

export async function completeRegistration(
  data: ICompleteRegistrationRequest,
  registrationToken: string,
): Promise<IAuthSessionResult> {
  const payload = await api.post<IApiEnvelope<IAuthSessionResult>>(
    ApiEndPoints.AUTH.REGISTER_COMPLETE,
    data,
    { headers: { "X-Registration-Token": registrationToken }, ...handledErrorConfig },
  );
  return payload.data!;
}

export async function passwordLogin(data: IPasswordLoginRequest): Promise<IAuthSessionResult> {
  const payload = await api.post<IApiEnvelope<IAuthSessionResult>>(
    ApiEndPoints.AUTH.PASSWORD_LOGIN,
    data,
    handledErrorConfig,
  );
  return payload.data!;
}

export async function passwordForgot(
  mobile: string,
  idempotencyKey?: string,
): Promise<IOtpRequestResult> {
  const payload = await api.post<IApiEnvelope<IOtpRequestResult>>(
    ApiEndPoints.AUTH.PASSWORD_FORGOT,
    { mobile },
    { headers: idempotencyHeaders(idempotencyKey), ...handledErrorConfig },
  );
  return payload.data!;
}

export async function passwordVerifyOtp(mobile: string, code: string): Promise<IResetTokenResult> {
  const payload = await api.post<IApiEnvelope<IResetTokenResult>>(
    ApiEndPoints.AUTH.PASSWORD_VERIFY,
    { mobile, code },
    handledErrorConfig,
  );
  return payload.data!;
}

export async function passwordReset(password: string, resetToken: string): Promise<void> {
  await api.post(
    ApiEndPoints.AUTH.PASSWORD_RESET,
    { password },
    {
      headers: { "X-Reset-Token": resetToken },
      ...handledErrorConfig,
    },
  );
}

export async function refreshSession(): Promise<IAuthSessionResult> {
  const payload = await api.post<IApiEnvelope<IAuthSessionResult>>(
    ApiEndPoints.AUTH.REFRESH_TOKEN,
    undefined,
    { headers: csrfHeaders() },
  );
  return payload.data!;
}

export async function revokeManagedSession(sessionId: string, manageToken: string): Promise<void> {
  await api.post(ApiEndPoints.AUTH.SESSION_REVOKE(sessionId), undefined, {
    headers: { "X-Session-Manage-Token": manageToken },
    ...handledErrorConfig,
  });
}

export async function exchangeImpersonationToken(token: string): Promise<IAuthTokenData> {
  const payload = await api.post<IApiEnvelope<IAuthTokenData>>(
    ApiEndPoints.AUTH.IMPERSONATION_EXCHANGE,
    undefined,
    {
      headers: { "X-Impersonation-Token": token },
      ...handledErrorConfig,
    },
  );
  return payload.data!;
}

export async function logout(): Promise<void> {
  try {
    await api.post(ApiEndPoints.AUTH.LOGOUT, undefined, { headers: csrfHeaders() });
  } finally {
    clearAuthCookies();
    clearFlow();
  }
}
